import * as Sentry from "@sentry/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
  useGetCmpEventRegistrationsQuery,
  useGetCmpEventSponsorshipRequestsQuery,
  useRecordCmpEventRegistrationManualPaymentMutation,
  useRecordCmpEventSponsorshipManualPaymentMutation,
} from "../../../../graphql/types";
import { refreshAfterConfirmedSave } from "../refreshAfterConfirmedSave";
import { useEventSalesReadback } from "../../Events/EventSales/eventSalesReadback";
import EventSecureCardCheckoutPanel from "../../Events/EventSales/EventSecureCardCheckoutPanel";
import { eventSalesHostedCheckoutApolloContext } from "../../Events/EventSales/eventSalesApolloErrorPolicy";
import { canStartEventSecureCardCheckout, getEventSecureCardCheckoutReadback, getEventSecureCardUnavailableMessage } from "../../Events/EventSales/eventSecureCardCheckout";
import {
  ProcessPaymentModal,
  ProcessPaymentSubmit,
} from "../../Events/EventSales/eventSalesShared";
import {
  getEventPaymentSuccessDetails,
  type EventPaymentSuccessDetails,
} from "../../Events/EventSales/eventPaymentSuccess";
import { runEventSalesOperation } from "../../Events/EventSales/eventSalesOperation";
import { isReceiptEligiblePaymentStatus } from "../invoiceFormatting";
import {
  getStaffPaymentMethodOptions,
  useStaffRecordablePaymentMethods,
} from "../staffPaymentMethods";
import { PaymentProcessedDialog } from "./ManageInvoiceActionModals";
import { getManageEventInvoiceIdentity } from "./manageEventInvoiceIdentity";
import {
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";
import { getEventInvoiceAccessText } from "./manageEventInvoicePermissions";

// A manual payment may have saved before the error, so staff check first.
const EVENT_PAYMENT_UNCONFIRMED_TEXT = `We could not confirm that the payment was recorded. Refresh the page and check the invoice before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
// The card payment went through; only reading the result failed.
const EVENT_CARD_RESULT_NOT_SHOWN_TEXT =
  "The card payment was received, but we could not show the result. Refresh the page to see the payment. Do not take the payment again.";
const EVENT_SALE_NOT_FOUND_TEXT =
  "We could not find the event sale for this invoice. Refresh the page and try again.";

type EventInvoiceKind =
  CmpInvoiceKindEnum.EventRegistration | CmpInvoiceKindEnum.EventSponsorship;

type ManageEventInvoicePaymentModalProps = Readonly<{
  actionAllowed: boolean;
  chamberId: string;
  creditCardAvailable?: boolean;
  customerName: string;
  detail?: CmpInvoiceDetailFieldsFragment | null;
  invoiceKind: EventInvoiceKind;
  invoiceNumber: string;
  onClose: () => void;
  onComplete: () => Promise<void> | void;
  onEmailReceipt?: (
    details: EventPaymentSuccessDetails,
  ) => Promise<void> | void;
  onError: (message: string) => void;
  onOpenReceiptPreview?: (
    details: EventPaymentSuccessDetails,
  ) => Promise<void> | void;
  open: boolean;
  paymentActionUnavailableReason?: string | null;
  receiptActionLoading?: boolean;
}>;

const ManageEventInvoicePaymentModal = ({
  actionAllowed,
  chamberId,
  creditCardAvailable = false,
  customerName,
  detail,
  invoiceKind,
  invoiceNumber,
  onClose,
  onComplete,
  onEmailReceipt,
  onError,
  onOpenReceiptPreview,
  open,
  paymentActionUnavailableReason,
  receiptActionLoading = false,
}: ManageEventInvoicePaymentModalProps): React.ReactElement | null => {
  const [operationInFlight, setOperationInFlight] = useState(false);
  const [paymentSuccess, setPaymentSuccess] =
    useState<EventPaymentSuccessDetails | null>(null);
  const invoice = detail?.invoice;
  const isRegistration = invoiceKind === CmpInvoiceKindEnum.EventRegistration;
  const identity = getManageEventInvoiceIdentity({
    chamberId,
    eventId: invoice?.eventId,
    eventRegistrationId: invoice?.eventRegistrationId,
    eventSponsorshipRequestId: invoice?.eventSponsorshipRequestId,
    invoiceId: invoice?.id,
    invoiceKind,
  });
  const normalizedChamberId = identity?.chamberId || "";
  const eventId = identity?.eventId || "";
  const invoiceId = identity?.invoiceId || "";
  const registrationId = isRegistration ? identity?.sourceId || "" : "";
  const sponsorshipRequestId = isRegistration
    ? ""
    : identity?.sourceId || "";

  const {
    data: registrationsData,
    loading: registrationsLoading,
  } = useGetCmpEventRegistrationsQuery({
    fetchPolicy: "cache-and-network",
    skip: !open || !isRegistration || !identity,
    variables: {
      input: {
        chamberId: normalizedChamberId || "CMP_SKIPPED",
        eventId: eventId || "CMP_SKIPPED",
      },
    },
  });
  const {
    data: sponsorshipsData,
    loading: sponsorshipsLoading,
  } = useGetCmpEventSponsorshipRequestsQuery({
    fetchPolicy: "cache-and-network",
    skip: !open || isRegistration || !identity,
    variables: {
      input: {
        chamberId: normalizedChamberId || "CMP_SKIPPED",
        eventId: eventId || "CMP_SKIPPED",
      },
    },
  });
  // Reads after a save keep a failed read on this popup instead of the
  // global error page (UX audit EVT-1).
  const { readRegistrations, readSponsorships } = useEventSalesReadback();
  const readbackInput = {
    chamberId: normalizedChamberId || "CMP_SKIPPED",
    eventId: eventId || "CMP_SKIPPED",
  };
  const registration = useMemo(
    () =>
      registrationsData?.getCmpEventRegistrations.registrations.find(
        (candidate) => candidate.id === registrationId,
      ) || null,
    [registrationId, registrationsData],
  );
  const sponsorship = useMemo(
    () =>
      sponsorshipsData?.getCmpEventSponsorshipRequests.sponsorshipRequests.find(
        (candidate) => candidate.id === sponsorshipRequestId,
      ) || null,
    [sponsorshipRequestId, sponsorshipsData],
  );

  const { recordableMethods } = useStaffRecordablePaymentMethods({
    chamberId: normalizedChamberId,
    skip: !open || !actionAllowed,
  });
  const manualPaymentMethods = useMemo(
    () =>
      actionAllowed
        ? getStaffPaymentMethodOptions({
            paymentRules: invoice?.paymentRules,
            recordableMethods,
          })
        : [],
    [actionAllowed, invoice?.paymentRules, recordableMethods],
  );
  const [recordRegistrationPayment, { loading: recordingRegistration }] =
    useRecordCmpEventRegistrationManualPaymentMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });
  const [recordSponsorshipPayment, { loading: recordingSponsorship }] =
    useRecordCmpEventSponsorshipManualPaymentMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });

  useEffect(() => {
    if (!open) {
      setPaymentSuccess(null);
    }
  }, [open]);

  const source = isRegistration ? registration : sponsorship;
  const sourcePaymentActionUnavailableReason = isRegistration
    ? registration?.paymentActionUnavailableReason
    : sponsorship?.paymentActionUnavailableReason;
  const effectivePaymentActionUnavailableReason =
    paymentActionUnavailableReason || sourcePaymentActionUnavailableReason;
  const identityError = identity
    ? null
    : EVENT_SALE_NOT_FOUND_TEXT;

  const sourceLoading = isRegistration
    ? registrationsLoading
    : sponsorshipsLoading;
  const sourceCardCheckoutAvailable = Boolean(
    invoice &&
      source &&
      source.invoiceId === invoice.id &&
      canStartEventSecureCardCheckout({
        amountDueInCents: invoice.amountDueInCents,
        invoiceStatus: invoice.invoiceStatus,
        paymentStatus: source.paymentStatus,
        purpose: isRegistration ? "REGISTRATION" : "SPONSORSHIP",
        sourceStatus: source.status,
      }),
  );
  // A paid invoice has nothing left to charge, so a closed card checkout is
  // expected there and is not an error (Sand Bay invoice 147, Oct 7 2026).
  const invoiceFullyPaid = Boolean(
    invoice &&
      (invoice.invoiceStatus === CmpInvoiceStatusEnum.Paid ||
        invoice.amountDueInCents <= 0),
  );
  // After a successful payment the registration reloads before the invoice,
  // so for a moment a PAID registration sits next to an UNPAID invoice. That
  // is not a blocked checkout (Sentry CHAMBER-MEMBER-PRO-52).
  const [paymentCompleting, setPaymentCompleting] = useState(false);
  const cardStateUnavailable = Boolean(
    !sourceLoading &&
      !paymentCompleting &&
      !paymentSuccess &&
      !invoiceFullyPaid &&
      source &&
      invoice?.paymentRules?.creditCardAllowed &&
      !effectivePaymentActionUnavailableReason &&
      (!creditCardAvailable || !sourceCardCheckoutAvailable),
  );
  const reportedCardState = useRef("");
  useEffect(() => {
    if (!open) {
      reportedCardState.current = "";
      setPaymentCompleting(false);
      return;
    }
    if (
      !cardStateUnavailable ||
      invoice?.invoiceStatus !== CmpInvoiceStatusEnum.Unpaid ||
      !source
    ) return;
    // This warning happens before a checkout request, so Apollo error reporting
    // cannot identify it. Keep the context limited to record IDs and gate state.
    const state = {
      chamberId: normalizedChamberId,
      eventId,
      invoiceId,
      invoiceKind,
      sourceId: source.id,
      sourceInvoiceId: source.invoiceId,
      invoiceStatus: invoice.invoiceStatus,
      paymentStatus: source.paymentStatus,
      sourceStatus: source.status,
      actionAllowed,
      parentCardAvailable: creditCardAvailable,
      sourceCardAvailable: sourceCardCheckoutAvailable,
      amountIsPositiveInteger:
        Number.isSafeInteger(invoice.amountDueInCents) && invoice.amountDueInCents > 0,
      mainBundle: document.querySelector('script[src*="/static/js/main."]')?.getAttribute("src") || null,
    };
    const stateKey = JSON.stringify(state);
    if (reportedCardState.current === stateKey) return;
    reportedCardState.current = stateKey;
    Sentry.withScope(scope => {
      scope.setTransactionName("ManageEventInvoiceCardAvailability");
      scope.setContext("cmpEventInvoiceCardAvailability", state);
      Sentry.captureMessage("Event invoice card checkout blocked before start", "warning");
    });
  }, [
    actionAllowed, cardStateUnavailable, creditCardAvailable, eventId, invoice,
    invoiceId, invoiceKind, normalizedChamberId, open, source, sourceCardCheckoutAvailable,
  ]);

  if (!open) {
    return null;
  }

  const handleRefresh = async () => {
    if (isRegistration) {
      const readback = await readRegistrations(readbackInput);
      return getEventSecureCardCheckoutReadback(
        readback.data?.getCmpEventRegistrations.registrations.find(candidate => candidate.id === registrationId),
        invoiceId,
      );
    }
    const readback = await readSponsorships(readbackInput);
    return getEventSecureCardCheckoutReadback(
      readback.data?.getCmpEventSponsorshipRequests.sponsorshipRequests.find(candidate => candidate.id === sponsorshipRequestId),
      invoiceId,
    );
  };

  const handleSubmit = async (payload: ProcessPaymentSubmit) => {
    if (
      !actionAllowed ||
      !invoice ||
      !identity ||
      !source ||
      effectivePaymentActionUnavailableReason
    ) {
      onError(
        effectivePaymentActionUnavailableReason ||
          identityError ||
          (actionAllowed
            ? EVENT_SALE_NOT_FOUND_TEXT
            : getEventInvoiceAccessText(invoiceKind)),
      );
      return;
    }

    setOperationInFlight(true);

    try {
      let successDetails: EventPaymentSuccessDetails | null = null;

      if (isRegistration) {
        if (!registrationId) {
          throw new UserFacingError(EVENT_SALE_NOT_FOUND_TEXT);
        }

        const result = await runEventSalesOperation({
          action: "RECORD_REGISTRATION_MANUAL_PAYMENT",
          chamberId,
          entityId: registrationId,
          eventId,
          execute: (input) =>
            recordRegistrationPayment({ variables: { input } }),
          input: {
            amountInCents: payload.amountInCents,
            chamberId: normalizedChamberId,
            eventId,
            invoiceId,
            isCmpEventModuleTestData:
              registration?.isCmpEventModuleTestData || undefined,
            manualPaymentMethodId: payload.manualPaymentMethodId,
            note: payload.note || undefined,
            receivedAtUtcMilli: payload.receivedAtUtcMilli,
            reference: payload.reference || undefined,
            registrationId,
          },
          isAuthoritativelyCompleted: (mutationResult) =>
            Boolean(
              mutationResult.data?.recordCmpEventRegistrationManualPayment
                .registrationSavedSuccessfully &&
              mutationResult.data.recordCmpEventRegistrationManualPayment
                .registration?.id === registrationId,
            ),
        });

        const savedRegistration =
          result.data?.recordCmpEventRegistrationManualPayment.registration;
        if (
          !result.data?.recordCmpEventRegistrationManualPayment
            .registrationSavedSuccessfully ||
          savedRegistration?.id !== registrationId
        ) {
          throw new Error("The ticket payment could not be confirmed.");
        }
        successDetails = getEventPaymentSuccessDetails(
          savedRegistration,
          customerName,
        );
      } else {
        if (!sponsorshipRequestId) {
          throw new UserFacingError(EVENT_SALE_NOT_FOUND_TEXT);
        }

        const result = await runEventSalesOperation({
          action: "RECORD_SPONSORSHIP_MANUAL_PAYMENT",
          chamberId,
          entityId: sponsorshipRequestId,
          eventId,
          execute: (input) =>
            recordSponsorshipPayment({ variables: { input } }),
          input: {
            amountInCents: payload.amountInCents,
            chamberId: normalizedChamberId,
            eventId,
            invoiceId,
            isCmpEventModuleTestData:
              sponsorship?.isCmpEventModuleTestData || undefined,
            manualPaymentMethodId: payload.manualPaymentMethodId,
            note: payload.note || undefined,
            receivedAtUtcMilli: payload.receivedAtUtcMilli,
            reference: payload.reference || undefined,
            sponsorshipRequestId,
          },
          isAuthoritativelyCompleted: (mutationResult) =>
            Boolean(
              mutationResult.data?.recordCmpEventSponsorshipManualPayment
                .sponsorshipRequestSavedSuccessfully &&
              mutationResult.data.recordCmpEventSponsorshipManualPayment
                .sponsorshipRequest?.id === sponsorshipRequestId,
            ),
        });

        const savedSponsorship =
          result.data?.recordCmpEventSponsorshipManualPayment
            .sponsorshipRequest;
        if (
          !result.data?.recordCmpEventSponsorshipManualPayment
            .sponsorshipRequestSavedSuccessfully ||
          savedSponsorship?.id !== sponsorshipRequestId
        ) {
          throw new Error("The sponsorship payment could not be confirmed.");
        }
        successDetails = getEventPaymentSuccessDetails(
          savedSponsorship,
          customerName,
        );
      }

      if (!successDetails) {
        throw new Error(
          "The saved event payment receipt could not be confirmed.",
        );
      }

      setPaymentCompleting(true);
      const refreshWarning = await refreshAfterConfirmedSave({
        refresh: async () => {
          await handleRefresh();
          await onComplete();
        },
        savedWhat: "The payment was recorded",
      });
      setPaymentSuccess(successDetails);
      if (refreshWarning) {
        onError(refreshWarning);
      }
    } catch (error) {
      const errorMessage = getUserFacingErrorText(
        error,
        EVENT_PAYMENT_UNCONFIRMED_TEXT,
      );
      setPaymentCompleting(false);
      onError(errorMessage);
      throw error instanceof Error ? error : new Error(errorMessage);
    } finally {
      setOperationInFlight(false);
    }
  };

  const handleCardPaymentProcessed = async () => {
    setPaymentCompleting(true);
    try {
      let successDetails: EventPaymentSuccessDetails | null;

      if (isRegistration) {
        const readback = await readRegistrations(readbackInput);
        const savedRegistration =
          readback.data?.getCmpEventRegistrations.registrations.find(
            (candidate) => candidate.id === registrationId,
          );
        successDetails = getEventPaymentSuccessDetails(
          savedRegistration,
          customerName,
        );
      } else {
        const readback = await readSponsorships(readbackInput);
        const savedSponsorship =
          readback.data?.getCmpEventSponsorshipRequests.sponsorshipRequests.find(
            (candidate) => candidate.id === sponsorshipRequestId,
          );
        successDetails = getEventPaymentSuccessDetails(
          savedSponsorship,
          customerName,
        );
      }

      if (!successDetails) {
        throw new Error(
          "The saved event payment receipt could not be confirmed.",
        );
      }

      await onComplete();
      setPaymentSuccess(successDetails);
    } catch (error) {
      const errorMessage = getUserFacingErrorText(
        error,
        EVENT_CARD_RESULT_NOT_SHOWN_TEXT,
      );
      // An unconfirmed result may be a real mismatch, so reporting resumes.
      setPaymentCompleting(false);
      onError(errorMessage);
      throw error instanceof Error ? error : new Error(errorMessage);
    }
  };

  const lineItems = detail?.lineItems || invoice?.lineItems || [];
  const savedReceiptPayment = paymentSuccess
    ? detail?.payments.find(
        (payment) =>
          payment.id === paymentSuccess.paymentId &&
          isReceiptEligiblePaymentStatus(payment.status),
      )
    : null;
  const canEmailSavedReceipt = Boolean(
    actionAllowed &&
    paymentSuccess &&
    savedReceiptPayment &&
    detail?.availableActions.some(
      (action) =>
        action.action === CmpInvoiceActionTypeEnum.EmailDocument &&
        action.available,
    ),
  );
  const canOpenSavedReceipt = Boolean(
    actionAllowed &&
    paymentSuccess &&
    savedReceiptPayment &&
    detail?.availableActions.some(
      (action) =>
        action.action === CmpInvoiceActionTypeEnum.DownloadPdf &&
        action.available,
    ),
  );

  return (
    <>
      <ProcessPaymentModal
        amountDueInCents={invoice?.amountDueInCents || 0}
        creditCardAvailable={Boolean(
          creditCardAvailable &&
            actionAllowed &&
            invoice?.paymentRules?.creditCardAllowed &&
            sourceCardCheckoutAvailable &&
            !effectivePaymentActionUnavailableReason,
        )}
        cardCheckoutUnavailableReason={
          cardStateUnavailable
            ? getEventSecureCardUnavailableMessage({
                fallback: "Card payment is not available for this invoice right now. Refresh the page and try again, or select another payment method.",
                paymentStatus: source?.paymentStatus,
                purpose: isRegistration ? "REGISTRATION" : "SPONSORSHIP",
                sourceStatus: source?.status,
              })
            : null
        }
        customerName={customerName}
        invoiceLineItems={lineItems}
        invoiceNumber={invoiceNumber}
        loading={
          operationInFlight ||
          recordingRegistration ||
          recordingSponsorship ||
          sourceLoading
        }
        onClose={onClose}
        onSubmit={handleSubmit}
        open={open && !paymentSuccess}
        paymentActionUnavailableReason={
          effectivePaymentActionUnavailableReason ||
          identityError ||
          (!source && !sourceLoading
            ? EVENT_SALE_NOT_FOUND_TEXT
            : null)
        }
        paymentMethods={actionAllowed && source ? manualPaymentMethods : []}
        requireManualPaymentConfirmation={false}
        renderSecureCardCheckout={({
          amountInCents,
          beforeSubmit,
          cardNote,
          onPaymentInProgressChange,
        }) => {
          if (
            !actionAllowed ||
            !invoice ||
            !identity ||
            !source ||
            effectivePaymentActionUnavailableReason
          ) {
            return null;
          }

          return isRegistration ? (
            <EventSecureCardCheckoutPanel
              onPaymentInProgressChange={onPaymentInProgressChange}
              key={`registration-checkout:${registrationId}:${invoiceId}:${amountInCents}`}
              amountDueInCents={amountInCents}
              beforeSubmit={beforeSubmit}
                    cardNote={cardNote}
              chamberId={normalizedChamberId}
              eventId={eventId}
              invoiceId={invoiceId}
              invoiceStatus={invoice.invoiceStatus}
              onPaymentProcessed={handleCardPaymentProcessed}
              onRefreshAuthoritativeStatus={handleRefresh}
              paymentStatus={registration!.paymentStatus}
              purpose="REGISTRATION"
              sourceId={registration!.id}
              sourceStatus={registration!.status}
            />
          ) : (
            <EventSecureCardCheckoutPanel
              onPaymentInProgressChange={onPaymentInProgressChange}
              key={`sponsorship-checkout:${sponsorshipRequestId}:${invoiceId}:${amountInCents}`}
              amountDueInCents={amountInCents}
              beforeSubmit={beforeSubmit}
                    cardNote={cardNote}
              chamberId={normalizedChamberId}
              eventId={eventId}
              invoiceId={invoiceId}
              invoiceStatus={invoice.invoiceStatus}
              onPaymentProcessed={handleCardPaymentProcessed}
              onRefreshAuthoritativeStatus={handleRefresh}
              paymentStatus={sponsorship!.paymentStatus}
              purpose="SPONSORSHIP"
              sourceId={sponsorship!.id}
              sourceStatus={sponsorship!.status}
            />
          );
        }}
        subtotalAmountInCents={invoice?.subtotalAmountInCents}
        taxAmountInCents={invoice?.taxAmountInCents}
        totalAmountInCents={invoice?.totalAmountInCents}
      />
      <PaymentProcessedDialog
        actionLoading={receiptActionLoading}
        billingContactEmail={paymentSuccess?.billingContactEmail}
        customerName={paymentSuccess?.customerName || customerName}
        onClose={() => {
          setPaymentSuccess(null);
          onClose();
        }}
        onEmailReceipt={
          canEmailSavedReceipt && paymentSuccess && onEmailReceipt
            ? () => onEmailReceipt(paymentSuccess)
            : undefined
        }
        onPrintReceipt={
          canOpenSavedReceipt && paymentSuccess && onOpenReceiptPreview
            ? () => onOpenReceiptPreview(paymentSuccess)
            : undefined
        }
        open={Boolean(paymentSuccess)}
      />
    </>
  );
};

export default ManageEventInvoicePaymentModal;
